import { NavLink } from 'react-router-dom';
import { useUIStore, useAuthStore, usePOSStore } from '@/stores';
import { useHasPermission } from '../auth/RequirePermission';
import { LayoutDashboard, Users, Briefcase, TrendingUp, Settings, ChevronLeft, LogOut, BookOpen, ShoppingCart, Calendar, Receipt } from 'lucide-react';

interface SidebarProps {
  isMobile?: boolean;
}

/* ─── Sidebar Component ─── */
export function Sidebar({ isMobile = false }: SidebarProps) {
  const storeCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebarCollapsed = useUIStore((s) => s.toggleSidebarCollapsed);
  const setMobileDrawerOpen = useUIStore((s) => s.setMobileDrawerOpen);
  const openPOS = usePOSStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const isOwner = useAuthStore((s) => s.isOwner);
  const signOut = useAuthStore((s) => s.signOut);
  const hasPermission = useHasPermission();

  // On mobile drawer, sidebar is NEVER collapsed (always expanded full width)
  const sidebarCollapsed = isMobile ? false : storeCollapsed;

  const canViewReports = hasPermission('reports:view');
  const canReadPersonal = hasPermission('staff:read');
  const canUpdateRates = hasPermission('rates:update');
  const canManageSettings = hasPermission('settings:manage');
  const canManageUsers = hasPermission('users:manage');

  const navItems = [
    ...(canViewReports ? [{ label: 'Dashboard', path: '/', icon: <LayoutDashboard className="h-4 w-4" /> }] : []),
    { label: 'Agenda', path: '/agenda', icon: <Calendar className="h-4 w-4" /> },
    { label: 'Ventas', path: '/ventas', icon: <Receipt className="h-4 w-4" /> },
    { label: 'Clientes', path: '/customers', icon: <Users className="h-4 w-4" /> },
    ...(canReadPersonal ? [{ label: 'Personal', path: '/staff', icon: <Briefcase className="h-4 w-4" /> }] : []),
    ...(canManageSettings ? [{ label: 'Catálogo', path: '/catalogo', icon: <BookOpen className="h-4 w-4" /> }] : []),
    ...(canUpdateRates ? [{ label: 'Tasas de Cambio', path: '/tasas', icon: <TrendingUp className="h-4 w-4" /> }] : []),
    ...(canManageSettings ? [{ label: 'Configuración', path: '/settings', icon: <Settings className="h-4 w-4" /> }] : []),
    ...(canManageUsers ? [{ label: 'Usuarios', path: '/usuarios', icon: <Users className="h-4 w-4" /> }] : []),
  ];

  /** On mobile, close the drawer when a link is clicked */
  const handleNavClick = () => {
    if (isMobile || window.innerWidth < 768) {
      setMobileDrawerOpen(false);
    }
  };

  const userInitials = user?.email
    ? user.email
        .split('@')[0]
        .split(/[._-]/)
        .slice(0, 2)
        .map((s) => s.charAt(0).toUpperCase())
        .join('')
    : 'U';

  const userName = profile?.name || user?.email?.split('@')[0] || 'Usuario';
  const userRoleText = isOwner ? 'Propietaria' : profile?.job_title || 'Operaciones';

  return (
    <aside
      className={`
        flex h-full flex-col bg-slate-950/95 backdrop-blur-xl border-r border-white/10
        transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] select-none
        ${sidebarCollapsed ? 'w-[72px]' : isMobile ? 'w-72 sm:w-80' : 'w-64'}
      `}
    >
      {/* ── Brand Header ── */}
      <div className="flex h-16 items-center gap-3 border-b border-white/10 px-4 shrink-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-orange via-amber-500 to-brand-orange-light text-lg font-black text-white shadow-glow-orange transition-transform hover:scale-105">
          D
        </div>
        <div
          className={`
            flex flex-col overflow-hidden whitespace-nowrap
            transition-[opacity,max-width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'}
          `}
        >
          <span className="text-base font-extrabold tracking-tight text-white leading-tight">
            Depilaway
          </span>
          <span className="text-[10px] font-semibold text-brand-sky/80 uppercase tracking-widest">
            Sistema de Gestión
          </span>
        </div>
      </div>

      {/* ── Navigation List ── */}
      <nav className="flex-1 space-y-1.5 overflow-y-auto p-3 custom-scrollbar">
        {/* POS Action Button */}
        <button
          type="button"
          onClick={() => { openPOS(); handleNavClick(); }}
          title={sidebarCollapsed ? 'Caja / POS' : undefined}
          className="group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 font-medium transition-all duration-200 btn-press bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-transparent text-emerald-400 border-l-4 border-emerald-500 shadow-xs hover:from-emerald-500/30"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base transition-all duration-200 bg-emerald-500 text-white shadow-md shadow-emerald-500/30 group-hover:scale-105">
            <ShoppingCart className="h-4 w-4" />
          </span>
          <span
            className={`
              overflow-hidden whitespace-nowrap text-sm font-semibold tracking-tight text-white
              transition-[opacity,max-width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'}
            `}
          >
            Caja / POS
          </span>
        </button>
        
        <div className="h-px w-full bg-white/10 my-2" />

        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            onClick={handleNavClick}
            title={sidebarCollapsed ? item.label : undefined}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-xl px-3 py-2.5
               font-medium transition-all duration-200 btn-press
               ${
                 isActive
                   ? 'bg-gradient-to-r from-brand-orange/25 via-brand-orange/15 to-transparent text-white border-l-4 border-brand-orange shadow-xs'
                   : 'text-brand-sky/80 hover:bg-white/8 hover:text-white'
               }`
            }
          >
            {({ isActive }) => (
              <>
                {/* Active indicator glow */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-orange shadow-glow-orange" />
                )}

                {/* Icon Container */}
                <span
                  className={`
                    flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base
                    transition-all duration-200
                    ${
                      isActive
                        ? 'bg-brand-orange text-white shadow-md shadow-brand-orange/30 scale-105'
                        : 'bg-white/5 group-hover:bg-white/12 group-hover:text-white group-hover:scale-105'
                    }
                  `}
                >
                  {item.icon}
                </span>

                {/* Label */}
                <span
                  className={`
                    overflow-hidden whitespace-nowrap text-sm font-semibold tracking-tight
                    transition-[opacity,max-width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
                    ${sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'}
                  `}
                >
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* ── Footer Section ── */}
      <div className={`border-t border-white/10 p-3 mt-auto shrink-0 ${
        isMobile ? 'pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]' : 'pb-3'
      }`}>
        {/* Mobile Drawer Profile Card */}
        {isMobile && (
          <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3 border border-white/8 shadow-xs">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-deep text-xs font-black text-white shadow-sm ring-2 ring-brand-orange/40">
              {userInitials}
            </div>
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <span className="truncate text-xs font-bold text-white leading-tight" title={userName}>
                {userName}
              </span>
              <span className="text-[10px] font-semibold text-brand-sky/70 truncate mt-0.5">
                {userRoleText}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setMobileDrawerOpen(false);
                void signOut();
              }}
              className="btn-press p-2 rounded-xl text-text-tertiary hover:bg-error/20 hover:text-error transition-all cursor-pointer shrink-0"
              title="Cerrar sesión"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Desktop Collapse Toggle */}
        {!isMobile && (
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            className="btn-press flex w-full items-center gap-3 rounded-xl px-3 py-2 text-brand-sky/70 transition-all duration-200 hover:bg-white/8 hover:text-white cursor-pointer"
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            title={sidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5">
              <ChevronLeft
                className={`h-4 w-4 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180 text-brand-orange' : ''}`}
              />
            </span>
            <span
              className={`
                overflow-hidden whitespace-nowrap text-xs font-bold tracking-wider uppercase text-brand-sky/90
                transition-[opacity,max-width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
                ${sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'}
              `}
            >
              {sidebarCollapsed ? 'Expandir' : 'Colapsar'}
            </span>
          </button>
        )}
      </div>
    </aside>
  );
}
